import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles';

import { Dialog } from 'components/common';
import { Input, Select, Switch, ToggleOptions } from 'components/form';
import { composeValidators, positive, required, requiredIf } from 'components/form/validationRules';
import NumberInputFieldWithActions from 'components/inputs/NumberInputFieldWithActions';

import ImageInputField from '../levels/ImageInputField';
import { RewardedActionDto } from '../../types';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    minWidth: theme.spacing(80),
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  root: {
    maxWidth: '820px',
  },
  layout: {
    display: 'flex',
    gap: '32px',
    flexWrap: 'wrap',
  },
  fieldsColumn: {
    flex: 1,
    minWidth: 200,
    paddingTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  toggleRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingTop: theme.spacing(1),
  },
  inputNoMargin: {
    marginTop: 0,
  },
}));

interface EditActionDialogProps {
  open: boolean;
  onClose: () => void;
  action?: RewardedActionDto;
}

interface FormValues {
  icon?: File | string | null;
  title: string;
  description: string;
  ctaUrl: string;
  ctaText: string;
  ctaId: string;
  pointsAwarded: string;
  type?: string;
  isInternal?: boolean;
  repeatable?: boolean;
}

interface RewardedActionCTAIdDto {
  id: string;
  description: string;
}

type CtaIdsMode = 'internal' | 'external';

interface CtaIdsModeState {
  items: RewardedActionCTAIdDto[];
  loaded: boolean;
  loading: boolean;
}

const getSystemIntegrationLabel = (value: string | null | undefined) => {
  if (!value) {
    return '';
  }

  const hasUnderscore = value.includes('_');
  const normalized = hasUnderscore
    ? value.split('_').filter(Boolean).join(' ')
    : value.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

  return normalized.trim();
};

const REPEATABLE_VISIBLE_TYPES = ['create_shipment', 'account_funding'];

const isRepeatableType = (value: string | null | undefined) =>
  value != null && REPEATABLE_VISIBLE_TYPES.includes(value);

const EditActionDialog = ({ open, onClose, action }: EditActionDialogProps) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const [systemIntegrations, setSystemIntegrations] = useState<string[]>([]);
  const [ctaIdsStateByMode, setCtaIdsStateByMode] = useState<Record<CtaIdsMode, CtaIdsModeState>>({
    internal: { items: [], loaded: false, loading: false },
    external: { items: [], loaded: false, loading: false },
  });
  const [isInternalMode, setIsInternalMode] = useState<boolean>(Boolean(action?.type));
  const ctaIdsRequestIdRef = useRef<Record<CtaIdsMode, number>>({ internal: 0, external: 0 });
  const isMountedRef = useRef(true);
  const ctaIdsActiveModeRef = useRef<CtaIdsMode>(isInternalMode ? 'internal' : 'external');
  const formRef = useRef<any>(null);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    ctaIdsActiveModeRef.current = isInternalMode ? 'internal' : 'external';
  }, [isInternalMode]);

  useEffect(() => {
    if (action?.id) {
      setIsInternalMode(Boolean(action?.type));
      return;
    }

    setIsInternalMode(false);
  }, [action?.id, action?.type]);

  useEffect(() => {
    let isMounted = true;

    const fetchSystemIntegrations = async () => {
      try {
        const { data } = await dataProvider.get('rewardsActionSystemTypes');
        if (!isMounted) {
          return;
        }

        setSystemIntegrations(Array.isArray(data) ? data : []);
      } catch (error: any) {
        const message = error?.message || 'Failed to load system integrations';
        notify(message, 'warning');
      }
    };

    fetchSystemIntegrations();

    return () => {
      isMounted = false;
    };
  }, [dataProvider, notify]);

  useEffect(() => {
    const mode: CtaIdsMode = isInternalMode ? 'internal' : 'external';
    const requestId = ctaIdsRequestIdRef.current[mode] + 1;
    ctaIdsRequestIdRef.current[mode] = requestId;

    setCtaIdsStateByMode((prev) => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        loading: true,
      },
    }));

    const fetchCtaIds = async () => {
      try {
        const params = mode === 'internal' ? { internal: null } : undefined;
        const { data } = await dataProvider.get('rewardsActionCtaIds', params);
        if (!isMountedRef.current) {
          return;
        }
        if (ctaIdsRequestIdRef.current[mode] !== requestId) {
          return;
        }

        setCtaIdsStateByMode((prev) => ({
          ...prev,
          [mode]: {
            items: Array.isArray(data) ? data : [],
            loaded: true,
            loading: false,
          },
        }));
      } catch (error: any) {
        if (!isMountedRef.current) {
          return;
        }
        if (ctaIdsRequestIdRef.current[mode] !== requestId) {
          return;
        }

        setCtaIdsStateByMode((prev) => ({
          ...prev,
          [mode]: {
            ...prev[mode],
            loading: false,
          },
        }));

        if (ctaIdsActiveModeRef.current === mode) {
          const message = error?.message || 'Failed to load CTA IDs';
          notify(message, 'warning');
        }
      }
    };

    fetchCtaIds();
  }, [dataProvider, notify, isInternalMode]);

  const initialValues: FormValues = useMemo(
    () => ({
      icon: action?.iconUrl ?? '',
      title: action?.title ?? '',
      description: action?.description ?? '',
      ctaUrl: action?.ctaUrl ?? '',
      ctaText: action?.ctaText ?? '',
      ctaId: action?.ctaId ?? '',
      pointsAwarded: action?.pointsAwarded?.toString() ?? '',
      type: action?.type ?? '',
      isInternal: Boolean(action?.type),
      repeatable: action?.repeatable ?? false,
    }),
    [action]
  );

  const systemIntegrationOptions = useMemo(
    () => systemIntegrations.map((value) => ({ value, label: getSystemIntegrationLabel(value) })),
    [systemIntegrations]
  );
  const ctaIdOptionsInternal = useMemo(
    () =>
      ctaIdsStateByMode.internal.items.map(({ id, description }) => ({
        value: id,
        label: description,
      })),
    [ctaIdsStateByMode.internal.items]
  );
  const ctaIdOptionsExternal = useMemo(
    () =>
      ctaIdsStateByMode.external.items.map(({ id, description }) => ({
        value: id,
        label: description,
      })),
    [ctaIdsStateByMode.external.items]
  );

  useEffect(() => {
    const form = formRef.current;
    if (!form || typeof form.getState !== 'function' || typeof form.change !== 'function') {
      return;
    }

    const mode: CtaIdsMode = Boolean(form.getState()?.values?.isInternal) ? 'internal' : 'external';
    const modeState = ctaIdsStateByMode[mode];
    if (!modeState.loaded) {
      return;
    }

    const ctaIdValue = form.getState()?.values?.ctaId;
    if (!ctaIdValue) {
      return;
    }

    const isValidForMode = modeState.items.some(({ id }) => id === ctaIdValue);
    if (!isValidForMode) {
      form.change('ctaId', '');
    }
  }, [ctaIdsStateByMode, isInternalMode]);

  const handleSubmitForm = async (values: FormValues) => {
    try {
      const formData = new FormData();
      const isInternal = action?.id ? Boolean(action?.type) : Boolean(values.isInternal);
      const isRepeatableEnabled = isInternal && isRepeatableType(values.type);

      formData.append('title', values.title?.trim() ?? '');
      formData.append('description', values.description?.trim() ?? '');
      const ctaUrlValue = isInternal ? '' : values.ctaUrl?.trim();
      const ctaTextValue = isInternal ? '' : values.ctaText?.trim();
      const ctaIdValue = values.ctaId?.trim();

      formData.append('ctaUrl', ctaUrlValue ?? '');
      formData.append('ctaText', ctaTextValue ?? '');
      formData.append('ctaId', ctaIdValue ?? '');
      formData.append('pointsAwarded', values.pointsAwarded ?? '0');
      // Assumption: the API accepts boolean values in FormData as 'true'/'false' strings.
      formData.append('repeatable', isRepeatableEnabled && values.repeatable ? 'true' : 'false');

      if (isInternal) {
        formData.append('type', values.type ?? '');
      } else {
        formData.append('type', '');
      }

      if (values.icon instanceof File) {
        formData.append('icon', values.icon);
      } else if (typeof values.icon === 'string' && values.icon) {
        formData.append('icon', values.icon);
      }

      const response = action?.id
        ? await dataProvider.update('rewardsAction', {
            id: action.id,
            data: formData,
          })
        : await dataProvider.post('rewardsAction', {
            data: formData,
          });

      refresh();
      onClose();

      return response;
    } catch (error: any) {
      const message = error?.message || 'Failed to save rewards action';
      notify(message, 'error');
      return { [FORM_ERROR]: message };
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Form<FormValues>
      key={action?.id ?? 'new-action'}
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      render={({ handleSubmit, submitting, submitError, values, form }) => {
        formRef.current = form;
        const isInternal = action?.id ? Boolean(action?.type) : Boolean(values.isInternal);
        const shouldShowRepeatable = isInternal && isRepeatableType(values.type);
        const ctaIdsMode: CtaIdsMode = isInternal ? 'internal' : 'external';
        const ctaIdOptions = isInternal ? ctaIdOptionsInternal : ctaIdOptionsExternal;
        const shouldDisableCtaId = !ctaIdOptions.length;

        return (
          <form onSubmit={handleSubmit}>
            <FormSpy
              subscription={{ values: true }}
              onChange={({ values }) => {
                if (action?.id) {
                  return;
                }

                const nextInternal = Boolean(values?.isInternal);
                if (nextInternal !== isInternalMode) {
                  setIsInternalMode(nextInternal);
                }
              }}
            />
            <Dialog
              open={open}
              onCancel={onClose}
              title={action?.id ? 'Edit action' : 'Add action'}
              onConfirm={handleSubmit}
              confirmText="Save"
              submitting={submitting}
              error={submitError}
              classes={{ content: classes.content, root: classes.root }}>
              <div className={classes.layout}>
                <ImageInputField name="icon" />
                <div className={classes.fieldsColumn}>
                  {!action?.id && (
                    <div className={classes.toggleRow}>
                      <ToggleOptions
                        name="isInternal"
                        options={[
                          { label: 'Internal', value: true },
                          { label: 'External', value: false },
                        ]}
                      />
                    </div>
                  )}
                  <Input
                    name="title"
                    label="Benefit"
                    validate={required}
                    classes={{ input: classes.inputNoMargin }}
                  />
                  <Input
                    name="description"
                    label="Description"
                    validate={required}
                    inputProps={{ multiline: true, rows: 4 }}
                  />
                  {!isInternal && <Input name="ctaUrl" label="Link" />}
                  {!isInternal && <Input name="ctaText" label="Button title" />}
                  <Select
                    key={ctaIdsMode}
                    name="ctaId"
                    label="Button action"
                    options={ctaIdOptions}
                    disabled={shouldDisableCtaId}
                  />
                  {isInternal && (
                    <Select
                      name="type"
                      label="System integration"
                      options={systemIntegrationOptions}
                      disabled={!systemIntegrationOptions.length}
                      validate={requiredIf(() => Boolean(isInternal))}
                    />
                  )}
                  <NumberInputFieldWithActions
                    name="pointsAwarded"
                    label="Points"
                    actionButtons
                    validate={composeValidators(required, positive)}
                  />
                  {shouldShowRepeatable && <Switch name="repeatable" label="Repeatable" />}
                </div>
              </div>
            </Dialog>
          </form>
        );
      }}
    />
  );
};

export default EditActionDialog;
